/**
 * Attribution capture for the public lead form.
 *
 * Leads submitted from the website used to be stored without a `source`, so the
 * database default ('facebook_leads') kicked in and website leads were counted
 * as Facebook Ads leads. This module captures where the visitor actually came
 * from, so web leads can be told apart from Facebook lead-form leads — which is
 * what makes an ad test (native FB form vs. landing page) measurable.
 *
 * The values land in the existing `source` and `campaign` columns, so the
 * admin Leads page filters them with no extra work.
 */

const STORAGE_KEY = 'lead_attribution_v1';

export interface LeadAttribution {
  /** Goes to leads.source — always 'web' for this form. */
  source: 'web';
  /** Goes to leads.campaign — utm_campaign, or a label inferred from the click id. */
  campaign: string | null;
}

interface StoredAttribution {
  campaign: string | null;
  /** First landing URL params, kept for debugging/manual inspection. */
  raw: Record<string, string>;
  capturedAt: string;
}

const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'ttclid',
  'variant',
];

/**
 * Derive a human-readable campaign label from whatever the URL carried.
 * Prefers an explicit utm_campaign; otherwise falls back to the ad platform
 * implied by the click id, so at minimum we know the traffic was paid.
 */
function deriveCampaign(params: Record<string, string>): string | null {
  if (params.utm_campaign) {
    // Keep utm_source/variant context when present — useful when the same
    // campaign runs across platforms or in an A/B test.
    const bits = [params.utm_campaign];
    if (params.utm_source) bits.push(params.utm_source);
    if (params.variant) bits.push(`variant:${params.variant}`);
    return bits.join(' · ');
  }
  if (params.fbclid) return 'Facebook (χωρίς utm_campaign)';
  if (params.gclid) return 'Google Ads (χωρίς utm_campaign)';
  if (params.ttclid) return 'TikTok (χωρίς utm_campaign)';
  if (params.utm_source) return params.utm_source;
  return null;
}

/**
 * Read tracking params from the current URL and remember them for this tab.
 * Call once on landing. Safe to call repeatedly — the first capture wins, so
 * navigating within the site does not overwrite the original referrer.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;

  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return; // already captured this visit

    const url = new URLSearchParams(window.location.search);
    const raw: Record<string, string> = {};
    for (const key of TRACKED_PARAMS) {
      const value = url.get(key);
      if (value) raw[key] = value.slice(0, 200);
    }

    // Nothing to remember — a direct visit. Store an empty marker so a later
    // in-site navigation doesn't get mistaken for a fresh landing.
    const stored: StoredAttribution = {
      campaign: deriveCampaign(raw),
      raw,
      capturedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // sessionStorage can throw in private mode — attribution is best-effort
  }
}

/** What to store alongside the lead. */
export function getAttribution(): LeadAttribution {
  if (typeof window === 'undefined') return { source: 'web', campaign: null };

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as StoredAttribution;
      return { source: 'web', campaign: parsed.campaign ?? null };
    }
  } catch {
    // fall through to the default below
  }

  return { source: 'web', campaign: null };
}
